import * as PIXI from "pixi.js";
import FXManager from "../Effects/FXManager";
import { fireLightningBolt } from "../Effects/fireLightningBolt";
import { FEATURES_CONFIG } from "../../featuresConfig";
import { Settings } from "../../settings";
import { isBonusSymbol } from "../../SymbolData";

// Reusable point — avoids heap allocation in hot path
const _tempPoint = new PIXI.Point();

// ─── Zeus Symbol → POT Bone Mapping ──────────────────────────────────────────

/**
 * Maps Zeus symbol IDs to their corresponding POT attachment bone names
 * in the game_view spine.
 *
 * 12 = cd (Double, Green Zeus) → attachment_double_pot
 * 13 = cm (Mystery, Red Zeus)  → attachment_mystery_pot
 * 14 = cc (Collect, Blue Zeus) → attachment_collect_pot
 */
const ZEUS_TO_POT_BONE = {
  12: "attachment_double_pot",
  13: "attachment_mystery_pot",
  14: "attachment_collect_pot",
};

/**
 * Maps Zeus symbol IDs to POT type strings for the onArrival callback.
 */
const ZEUS_TO_POT_TYPE = {
  12: "double",
  13: "mystery",
  14: "collect",
};

// ─── PotChargingAnimator ─────────────────────────────────────────────────────

/**
 * PotChargingAnimator — fires RevoltFX lightning bolt strikes from Zeus symbol
 * positions to matching POT attachment bone positions.
 *
 * Lightning bolt pattern: slingshot with 3 phases.
 *   1. CHARGE — energy ball at coin, bolt pulls back slightly behind coin
 *   2. TRAVEL — bolt shoots straight from coin to pot with snap jitter
 *   3. IMPACT — bolt shrinks at pot, impact ball appears
 *
 * **Responsive design:**
 * All positions are normalized to symbol-relative units at fire time and
 * recomputed per-frame from live symbolWidth/Height. Bolt and ball emitter
 * scales adapt to viewport via `symbolHeight / Settings.SYMBOL_HEIGHT`.
 *
 * Usage:
 *   const animator = new PotChargingAnimator(trailContainer, gameViewSpine);
 *   animator.fireTrail(symbolId, startPos, { onArrival });
 *   animator.cancel();  // Kill all active bolts
 *   animator.destroy(); // Full cleanup
 */
class PotChargingAnimator {
  /**
   * @param {PIXI.Container} trailContainer - Container to render lightning bolts into
   * @param {object|null} gameViewSpine - The game_view Spine instance for bone position lookups
   * @param {number} symbolWidth - Current symbol cell width
   * @param {number} symbolHeight - Current symbol cell height
   */
  constructor(trailContainer, gameViewSpine, symbolWidth, symbolHeight) {
    this.trailContainer = trailContainer;
    this.gameViewSpine = gameViewSpine;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;

    this._activeBolts = [];

    // Per-spin dedup — tracks which pot types have already fired playCollect().
    // GDD §5.2: +1 per Zeus type per spin, not per symbol.
    // Multiple bolts of the same type still render visually (lightning strikes)
    // but only the first triggers the actual stage increment.
    this._collectedTypes = new Set();

    // Spin generation counter — incremented on each resetCollectDedup().
    // Each trail captures the generation at fire time. On arrival, stale
    // trails (from a previous spin) are silently ignored — the visual bolt
    // still renders, but onArrival is never called, preventing +1 drift.
    this._spinGeneration = 0;
  }

  // ── Responsive Scale ────────────────────────────────────────────────────────

  /**
   * Compute the responsive scale factor based on current symbol height
   * vs the design-time reference height. This ensures bolt and ball effects
   * scale proportionally with the viewport.
   *
   * Same pattern as CoinCollectAnimator (line 210, 245):
   *   `const spineScale = this.symbolHeight / Settings.SYMBOL_HEIGHT;`
   *
   * @returns {number} Scale factor (1.0 at design-time dimensions)
   */
  _getResponsiveScale() {
    return this.symbolHeight / Settings.SYMBOL_HEIGHT;
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /**
   * Fire a slingshot lightning bolt from a Zeus symbol position to its matching POT.
   *
   * 3-phase animation: charge (pullback) → straight travel → impact at pot.
   * Positions are normalized to symbol-relative units at fire time and
   * recomputed per-frame from live dimensions for resize resilience.
   *
   * @param {number} symbolId - Zeus symbol ID (12=cd, 13=cm, 14=cc)
   * @param {{x: number, y: number}} startPos - Symbol center in trailContainer's local space
   * @param {object} [options]
   * @param {function(string)} [options.onArrival] - Called with POT type when bolt completes
   * @param {number} [options.duration] - Override travel duration (ms)
   * @returns {Promise<void>} Resolves when bolt animation is complete
   */
  fireTrail(symbolId, startPos, options = {}) {
    if (!this.trailContainer || !isBonusSymbol(symbolId)) {
      return Promise.resolve();
    }

    const boneName = ZEUS_TO_POT_BONE[symbolId];
    const potType = ZEUS_TO_POT_TYPE[symbolId];
    if (!boneName || !potType) {
      return Promise.resolve();
    }

    const endPos = this._getPotBonePosition(boneName);
    if (!endPos) {
      return Promise.resolve();
    }

    // Lift bolt target to center of pot cloud — bone sits at the base
    endPos.y -= this.symbolHeight * 0.15;

    const config = FEATURES_CONFIG.POT_CHARGING;
    const duration = options.duration || config.TRAIL_DURATION;

    return fireLightningBolt(this.trailContainer, startPos, endPos, {
      symbolIndex: options.symbolIndex,
      getDimensions: () => ({ symbolWidth: this.symbolWidth, symbolHeight: this.symbolHeight }),
      duration,
      activeBolts: this._activeBolts,
    }).then(() => {
      // onArrival is visual-only (playHitEffect) — always fire on bolt arrival.
      // Stage progression is driven by API sync, so no double-counting risk.
      options.onArrival?.(potType);
    });
  }


  /**
   * Cancel all active bolts immediately. No fade-out.
   * Resolves all pending promises to prevent dangling awaits.
   */
  cancel() {
    for (const bolt of this._activeBolts) {
      if (bolt.tickerFn) {
        PIXI.Ticker.shared.remove(bolt.tickerFn);
        bolt.tickerFn = null;
      }
      FXManager.releaseOwnedFx(bolt.boltData);
      // Resolve dangling promise so awaiting code doesn't hang
      bolt.resolve?.();

      // Clean up launch and impact ball emitters
      for (const ball of [bolt.launchBall, bolt.impactBall]) {
        FXManager.releaseOwnedFx(ball);
      }
    }
    this._activeBolts = [];
  }

  /**
   * Reset the per-spin collect dedup set.
   * Call at the start of each spin before reels land.
   */
  resetCollectDedup() {
    this._collectedTypes.clear();
    this._spinGeneration++;
  }

  /**
   * Returns a Promise that resolves when ALL in-flight bolts have completed.
   * Resolves immediately if no bolts are active.
   * Used by win controller to gate win processing until bolts finish.
   */
  waitForAllTrails() {
    if (this._activeBolts.length === 0) return Promise.resolve();
    return Promise.all(this._activeBolts.map((b) => new Promise((resolve) => {
      const originalResolve = b.resolve;
      b.resolve = () => {
        originalResolve?.();
        resolve();
      };
    })));
  }

  /**
   * Update symbol dimensions on resize.
   * @param {number} symbolWidth
   * @param {number} symbolHeight
   */
  resize(symbolWidth, symbolHeight) {
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
  }

  /**
   * Update the game_view spine reference (in case of re-creation).
   * @param {object} spine
   */
  setSpine(spine) {
    this.gameViewSpine = spine;
  }

  /**
   * Full cleanup — call on unmount.
   */
  destroy() {
    this.cancel();
    this.trailContainer = null;
    this.gameViewSpine = null;
  }

  // ── Private: Bone Position Resolver ────────────────────────────────────────

  /**
   * Get the position of a POT attachment bone in the trailContainer's
   * local coordinate space.
   *
   * Strategy:
   *   1. Find the bone on the game_view spine
   *   2. Get bone world transform (spine-local world coords)
   *   3. Convert to PIXI global coords using the spine's transform
   *   4. Convert from global to trailContainer's local coords
   *
   * @param {string} boneName - e.g. "attachment_double_pot"
   * @returns {{x: number, y: number}|null}
   */
  _getPotBonePosition(boneName) {
    const spine = this.gameViewSpine;
    if (!spine || !spine.skeleton) return null;

    const bone = spine.skeleton.findBone(boneName);
    if (!bone) return null;

    _tempPoint.set(bone.worldX, bone.worldY);
    const spineGlobalPos = spine.toGlobal(_tempPoint);

    if (!this.trailContainer || this.trailContainer.destroyed) return null;
    const localPos = this.trailContainer.toLocal(spineGlobalPos);

    return { x: localPos.x, y: localPos.y };
  }
}

export default PotChargingAnimator;
